
import { computed, defineComponent, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  CommentType,
  OldConsultationType,
  OldObservationHistoryType,
  OldResultAnalyzesType,
  RemoteAnalyzeType,
  RemoteConsultationType,
  RemoteObservationType,
  ReportDetailType,
} from '@/shared/types'
import { showErrorMessage } from '@/shared/utils'
import { patientApi, reportApi, userApi } from '@/shared/api'
import { useReportListing } from '@/shared/composables'
import { REPORT_VIEW } from '@/shared/constants'
import { WReportHeader } from '@/widgets/WReportHeader'
import { WReportImages } from '@/widgets/WReportImages'
import { WReportComments } from '@/widgets/WReportComments'
import { EOldObservationHistory } from '@/entities/EOldObservationHistory'
import { EOldResultAnalyzes } from '@/entities/EOldResultAnalyzes'
import { EOldRecommendations } from '@/entities/EOldRecommendations'
import { WReportSubmit } from '@/widgets/WReportSubmit'

export default defineComponent({
  name: 'OldReportPage',
  components: {
    WReportComments,
    WReportHeader,
    WReportImages,
    WReportSubmit,
    EOldRecommendations,
    EOldObservationHistory,
    EOldResultAnalyzes,
  },

  setup() {
    const route = useRoute()

    const { setUpdateReportListing } = useReportListing()

    const loading = ref(false)
    const analyzesLoading = ref(false)
    const email = ref('qwe@qwe.qwe')

    const reportInfo = ref<ReportDetailType>({
      id: 0,
      date_at: '',
      images: [],
      organization: undefined,
      doctor: undefined,
    })

    const resultAnalyzes = reactive<OldResultAnalyzesType>({
      index: {
        color: '',
        day: 0,
        month: 0,
        week: 0,
      },
      stoneList: [],
    })

    const consultation = reactive<OldConsultationType>({
      consultations: [],
      fullConsultationList: undefined,
      reCleaning: 0,
      brushType: 0,
      toothPasteType: [],
      homeTools: [],
    })

    const observationHistory = ref<OldObservationHistoryType[]>([])
    const comments = ref<CommentType[]>([])

    let userId: number

    const reportId = computed<string | string[]>(() => route.params.reportId)
    const patientId = computed<string | string[]>(() => route.params.patientId)

    watch(reportId, (newReportId) => {
      if (route.name === 'OldReportPage' && newReportId) {
        getFullInfo()
      }
    })

    const getFullInfo = async (): Promise<void> => {
      await getReportInfo()
      getUserInfo()
    }

    const getReportInfo = async (): Promise<void> => {
      loading.value = true

      const [error, response] = await reportApi.getReport(
        Number(reportId.value)
      )

      if (error) {
        showErrorMessage(error)
      } else {
        const report = response.data.report
        reportInfo.value = report
        comments.value = response.data.comments

        userId = report.user_id

        getConsultationList(response.data)
        await loadAnalyzesResult()

        if (patientId.value && patientId.value !== 'null-patient') {
          getObservationHistory(response.data.observation_history)
          await getPatientEmail()
        }
      }

      loading.value = false
    }

    const getAnalyzesResult = async (
      remoteReport: RemoteAnalyzeType
    ): Promise<void> => {
      const indexData = JSON.parse(remoteReport.index)

      if (indexData) {
        resultAnalyzes.index.color = indexData.color
        resultAnalyzes.index.day = Math.round(indexData.day * 100)
        resultAnalyzes.index.month = Math.round(indexData.month * 100)
        resultAnalyzes.index.week = Math.round(indexData.week * 100)
      } else {
        resultAnalyzes.index.color = 'green'
        resultAnalyzes.index.day = 0
        resultAnalyzes.index.month = 0
        resultAnalyzes.index.week = 0
      }

      const fullStoneList = await getStoneList()

      const teethStoneId = JSON.parse(remoteReport.stone)

      resultAnalyzes.stoneList = []

      if (teethStoneId) {
        teethStoneId.forEach((index) => {
          resultAnalyzes.stoneList.push(fullStoneList[Number(index)])
        })
      }
    }

    const getObservationHistory = (
      remoteObservationHistory: RemoteObservationType[]
    ): void => {
      observationHistory.value = []

      remoteObservationHistory.forEach((historyItem) => {
        const index = JSON.parse(historyItem.index)

        if (index) {
          const newItem: OldObservationHistoryType = {
            dateAt: historyItem.date_at,
            day: Math.round(index.day * 100),
            week: Math.round(index.week * 100),
            month: Math.round(index.month * 100),
            result: index
              ? Math.round((index.day + index.week + index.month) * 100)
              : 0,
          }

          observationHistory.value.push(newItem)
        }
      })
    }

    const getConsultationList = (
      remoteConsultation: RemoteConsultationType
    ): void => {
      consultation.fullConsultationList = remoteConsultation.consultations

      consultation.consultations = remoteConsultation.report.consultation
        ? JSON.parse(remoteConsultation.report.consultation)
        : []
      consultation.homeTools = remoteConsultation.report.home_tools
        ? JSON.parse(remoteConsultation.report.home_tools)
        : []
      consultation.brushType = remoteConsultation.report.brush_type
        ? JSON.parse(remoteConsultation.report.brush_type)
        : []
      consultation.toothPasteType = remoteConsultation.report.toothpaste_type
        ? JSON.parse(remoteConsultation.report.toothpaste_type)
        : []

      consultation.reCleaning = remoteConsultation.report.re_cleaning ?? 0
    }

    const loadAnalyzesResult = async (): Promise<void> => {
      if (!reportId.value) return

      const [error, response] = await reportApi.getReport(
        Number(reportId.value)
      )

      if (error) {
        showErrorMessage(error)
      } else if (response.data.color) {
        await getAnalyzesResult(response.data.report)
      } else {
        getAnalyzesResultEveryThreeSeconds()
      }
    }

    const getAnalyzesResultEveryThreeSeconds = (): void => {
      const MAX_COUNT_REQUESTS = 14
      let countRequests = 0

      analyzesLoading.value = true

      const interval = setInterval(async () => {
        if (!reportId.value) {
          clearInterval(interval)
          return
        }

        const [error, response] = await reportApi.getReport(
          Number(reportId.value)
        )

        if (error) {
          showErrorMessage(error)
        } else {
          countRequests++

          if (
            countRequests === MAX_COUNT_REQUESTS ||
            response.data.report.index
          ) {
            await getAnalyzesResult(response.data.report)

            setUpdateReportListing(true)

            analyzesLoading.value = false
            clearInterval(interval)
          }
        }
      }, 3000)
    }

    const getStoneList = async (): Promise<string[]> => {
      const [error, response] = await reportApi.getStoneData()

      if (error) {
        showErrorMessage(error)

        return []
      }

      return response.data.stone
    }

    const getPatientEmail = async (): Promise<void> => {
      const [error, response] = await patientApi.getPatientInfo(
        Number(patientId.value)
      )

      if (error) {
        showErrorMessage(error)
      } else {
        email.value = response.data.patient.email
      }
    }

    const getUserInfo = async (): Promise<void> => {
      const [error, response] = await userApi.getUser(userId)

      if (error) {
        showErrorMessage(error)
      } else {
        reportInfo.value.doctor = response.data.user
      }
    }

    onMounted((): void => {
      getFullInfo()
    })

    return {
      REPORT_VIEW,
      route,
      resultAnalyzes,
      consultation,
      loading,
      analyzesLoading,
      reportInfo,
      patientId,
      observationHistory,
      comments,
      email,
      getReportInfo,
    }
  },
})
